import React, { useEffect, useMemo, useState } from 'react';
import {
  LayoutChangeEvent,
  Platform,
  Pressable,
  StyleSheet,
  Text,
  View,
  ViewStyle,
} from 'react-native';
import type { BottomTabBarProps } from '@react-navigation/bottom-tabs';
import Animated, {
  useAnimatedStyle,
  useSharedValue,
  withSpring,
} from 'react-native-reanimated';
import MaterialCommunityIcons from '@react-native-vector-icons/material-design-icons/static';
import { GTFonts, palette, useTheme, useThemeColors } from '../../theme';
import { fs, iconSize, s, v } from '../../utils/libs';

type BackOfficeExpandableTabBarProps = BottomTabBarProps & {
  firstRowSlots?: number;
  expandedColumns?: number;
  autoShrinkOnTabPress?: boolean;
};

type RouteItem = {
  key: string;
  name: string;
  params?: object;
};

const DEFAULT_FIRST_ROW_SLOTS = 6; // includes the More/Less button
const DEFAULT_EXPANDED_COLUMNS = 6;
/** Narrow icon-only control; keeps overflow-row spacer aligned with first-row More. */
const MORE_BUTTON_WIDTH = s(28);

/** Matches previous LinearTransition.springify() — single spring for expand/collapse symmetry */
const TAB_BAR_SPRING = {
  damping: 12,
  stiffness: 280,
  overshootClamping: true as const,
};

const BackOfficeExpandableTabBar: React.FC<BackOfficeExpandableTabBarProps> = ({
  state,
  descriptors,
  navigation,
  firstRowSlots = DEFAULT_FIRST_ROW_SLOTS,
  expandedColumns = DEFAULT_EXPANDED_COLUMNS,
  autoShrinkOnTabPress = true,
}) => {
  const { theme } = useTheme();
  const colors = useThemeColors();
  /** Native back office: overflow row starts closed; user taps More to show extra tabs. */
  const [isExpanded, setIsExpanded] = useState(false);

  const routes = state.routes as RouteItem[];
  const hasOverflow = routes.length > firstRowSlots - 1;
  const primaryLimit = Math.max(1, firstRowSlots - 1);
  const primaryRoutes = hasOverflow ? routes.slice(0, primaryLimit) : routes;
  const overflowRoutes = hasOverflow ? routes.slice(primaryLimit) : [];

  const overflowRoutesKey = useMemo(
    () => overflowRoutes.map(r => `${r.key}:${r.name}`).join('|'),
    [overflowRoutes],
  );

  const [overflowContentHeight, setOverflowContentHeight] = useState(0);
  const overflowAnimatedHeight = useSharedValue(0);

  const firstRowColumns = hasOverflow
    ? firstRowSlots
    : Math.max(1, primaryRoutes.length);
  const overflowColumns = Math.max(1, expandedColumns);

  useEffect(() => {
    setOverflowContentHeight(0);
    overflowAnimatedHeight.value = 0;
    setIsExpanded(false);
  }, [overflowRoutesKey, overflowAnimatedHeight]);

  useEffect(() => {
    if (overflowContentHeight <= 0) {
      return;
    }
    overflowAnimatedHeight.value = withSpring(
      isExpanded ? overflowContentHeight : 0,
      TAB_BAR_SPRING,
    );
  }, [isExpanded, overflowContentHeight]);

  const animatedOverflowClipStyle = useAnimatedStyle(() => ({
    height: overflowAnimatedHeight.value,
    overflow: 'hidden',
  }));

  const onTabPress = (route: RouteItem, focused: boolean) => {
    const event = navigation.emit({
      type: 'tabPress',
      target: route.key,
      canPreventDefault: true,
    });

    if (!focused && !event.defaultPrevented) {
      navigation.navigate(route.name, route.params);
    }

    if (isExpanded && autoShrinkOnTabPress) {
      setIsExpanded(false);
    }
  };

  const onTabLongPress = (route: RouteItem) => {
    navigation.emit({
      type: 'tabLongPress',
      target: route.key,
    });
  };

  const renderTabButton = (
    route: RouteItem,
    routeIndex: number,
    columns: number,
    style?: ViewStyle,
    useFlex?: boolean,
  ) => {
    const descriptor = descriptors[route.key];
    const options = descriptor.options;
    const focused = state.index === routeIndex;
    const activeColor = options.tabBarActiveTintColor ?? colors.primary;
    const inactiveColor =
      options.tabBarInactiveTintColor ?? theme.colors.textGray700;
    const tintColor = focused ? activeColor : inactiveColor;
    const showLabel = options.tabBarShowLabel !== false;

    const rawLabel = options.tabBarLabel;
    const label =
      typeof rawLabel === 'string'
        ? rawLabel
        : typeof options.title === 'string'
          ? options.title
          : route.name;

    const icon =
      typeof options.tabBarIcon === 'function'
        ? options.tabBarIcon({
            focused,
            color: tintColor,
            size: iconSize(20),
          })
        : null;

    return (
      <Pressable
        key={route.key}
        accessibilityState={focused ? { selected: true } : {}}
        accessibilityLabel={options.tabBarAccessibilityLabel}
        testID={options.tabBarButtonTestID}
        onPress={() => onTabPress(route, focused)}
        onLongPress={() => onTabLongPress(route)}
        style={[
          styles.tabItem,
          useFlex
            ? {
                flex: 1,
                minWidth: 0,
                borderRadius: theme.borderRadius.sm,
              }
            : {
                width: `${100 / columns}%`,
                borderRadius: theme.borderRadius.sm,
              },
          style,
        ]}>
        <View style={styles.iconWrap}>
          {focused && <View pointerEvents="none" style={styles.iconPill} />}
          {icon}
        </View>
        {showLabel ? (
          <Text
            numberOfLines={1}
            style={[
              styles.label,
              {
                color: tintColor,
              },
            ]}>
            {label}
          </Text>
        ) : null}
      </Pressable>
    );
  };

  /**
   * Same overflow layout as pre-refactor git: one flex row with wrap, flex:1 tabs
   * + trailing spacer (aligns with More button width). Spring + measure/clip unchanged.
   */
  const renderOverflowRows = () => {
    if (overflowRoutes.length === 0) {
      return null;
    }
    return (
      <View style={styles.rowWrap}>
        {overflowRoutes.map((route, idx) =>
          renderTabButton(
            route,
            primaryLimit + idx,
            overflowColumns,
            undefined,
            true,
          ),
        )}
        <View style={styles.overflowSpacer} />
      </View>
    );
  };

  const onMeasureLayerLayout = (e: LayoutChangeEvent) => {
    const h = e.nativeEvent.layout.height;
    if (h > 0) {
      setOverflowContentHeight(prev => (Math.abs(prev - h) > 0.5 ? h : prev));
    }
  };

  const onClippedInnerLayout = (e: LayoutChangeEvent) => {
    const h = e.nativeEvent.layout.height;
    if (h > 0) {
      setOverflowContentHeight(prev => (Math.abs(prev - h) > 1 ? h : prev));
    }
  };

  return (
    <View
      style={[
        styles.container,
        {
          backgroundColor: theme.colors.surface,
          borderTopColor: theme.colors.border,
          // No paddingBottom here (matches pre-refactor) — avoids extra gap under tab row.
        },
      ]}>
      <View style={styles.row}>
        {primaryRoutes.map((route, idx) =>
          renderTabButton(route, idx, firstRowColumns, undefined, hasOverflow),
        )}
        {hasOverflow ? (
          <Pressable
            accessibilityRole="button"
            accessibilityLabel={
              isExpanded ? 'Collapse extra tabs' : 'Expand more tabs'
            }
            onPress={() => setIsExpanded(prev => !prev)}
            style={[
              styles.tabItem,
              styles.moreButton,
              {
                width: MORE_BUTTON_WIDTH,
                flexShrink: 0,
              },
            ]}>
            <View style={styles.iconWrap}>
              <MaterialCommunityIcons
                  name={
                    isExpanded ? 'chevron-down-circle' : 'chevron-up-circle'
                  }
                size={iconSize(22)}
                color={isExpanded ? colors.primary : theme.colors.textGray700}
              />
            </View>
            {/* Reserve label-row height so the icon lines up with labeled tabs */}
            <View style={styles.moreLabelSpacer} />
          </Pressable>
        ) : null}
      </View>

      {hasOverflow ? (
        <>
          {overflowContentHeight === 0 ? (
            <View
              pointerEvents="none"
              style={styles.measureSlot}
              collapsable={false}>
              <View style={styles.measureInner} onLayout={onMeasureLayerLayout}>
                {renderOverflowRows()}
              </View>
            </View>
          ) : (
            <Animated.View
              style={animatedOverflowClipStyle}
              pointerEvents={isExpanded ? 'auto' : 'none'}>
              <View
                style={[
                  styles.overflowInnerAbsolute,
                  overflowContentHeight > 0
                    ? { height: overflowContentHeight }
                    : undefined,
                ]}
                onLayout={onClippedInnerLayout}>
                {renderOverflowRows()}
              </View>
            </Animated.View>
          )}
        </>
      ) : null}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    borderTopWidth: StyleSheet.hairlineWidth,
    paddingTop: v(6),
    paddingHorizontal: s(4),
    position: 'relative',
    ...(Platform.OS !== 'web' && {
      shadowColor: '#000',
      shadowOffset: { width: 0, height: v(-2) },
      shadowOpacity: 0.08,
      shadowRadius: s(4),
      elevation: 6,
    }),
  },
  /** Takes no layout space; inner lays out at full width for height measurement */
  measureSlot: {
    height: 0,
    overflow: 'visible',
    zIndex: -1,
    width: '100%',
  },
  measureInner: {
    position: 'absolute',
    left: 0,
    right: 0,
    top: 0,
    opacity: 0,
    width: '100%',
  },
  row: {
    flexDirection: 'row',
    alignItems: 'stretch',
    justifyContent: 'flex-start',
  },
  /** Matches legacy BackOfficeExpandableTabBar rowWrap (git): 5 flex tabs + spacer slot */
  rowWrap: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    alignItems: 'stretch',
    justifyContent: 'flex-start',
    marginTop: v(2),
    width: '100%',
  },
  overflowSpacer: {
    width: MORE_BUTTON_WIDTH,
    flexShrink: 0,
  },
  overflowInnerAbsolute: {
    position: 'absolute',
    left: 0,
    right: 0,
    top: 0,
  },
  tabItem: {
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: v(56),
    paddingVertical: v(4),
    paddingHorizontal: s(2),
  },
  iconWrap: {
    minHeight: v(24),
    justifyContent: 'center',
    alignItems: 'center',
  },
  iconPill: {
    position: 'absolute',
    width: s(44),
    height: v(28),
    borderRadius: v(14),
    backgroundColor: palette.blue[50], // #E8F2FF soft brand tint (matches POS tab bar)
  },
  label: {
    marginTop: v(2),
    fontFamily: GTFonts.regular,
    fontSize: fs(12),
    lineHeight: fs(16),
    textAlign: 'center',
  },
  moreButton: {
    borderWidth: StyleSheet.hairlineWidth,
    borderColor: 'transparent',
    paddingHorizontal: 0,
  },
  moreLabelSpacer: {
    marginTop: v(2),
    height: fs(16),
  },
});

export default React.memo(BackOfficeExpandableTabBar);
